import { apiRequest } from '../../api/client';

const unwrap = <T>(promise: Promise<{ data: T }>): Promise<T> => promise.then((e) => e.data);

export type TierCycle = 'Lifetime' | 'CalendarYear' | 'SixMonths' | 'TwelveMonths';

export type TierDowngradePolicy = 'None' | 'OneLevel' | 'Recalculate';

export interface TierLevel {
  code: string;
  name: string;
  threshold: number;
  queuePriority: boolean;
  /**
   * Phần trăm giảm trên hóa đơn cho bậc này (BR-308), 0 là không có.
   *
   * POS áp giảm giá, hệ thống này chỉ nói cho thu ngân biết phải nhập bao nhiêu — con số hiện ở
   * bước tra cứu khách, TRƯỚC khi in hóa đơn.
   */
  discountPercent: number;
  iconUrl?: string | null;
  benefits?: string | null;
  /**
   * Màu hạng, một mã trong `allowedColors`. Response luôn có giá trị (BE gán mặc định theo thứ tự
   * hạng); trên form mới thì có thể rỗng — nghĩa là "để máy chủ chọn màu mặc định".
   */
  colorHex?: string | null;
}

/** Một màu trong bảng màu hạng. Bảng do máy chủ giữ — KHÔNG chép sang theme.ts. */
export interface TierColor {
  name: string;
  hex: string;
}

export interface TierDefinition {
  id: string;
  version: string;
  levels: TierLevel[];
  cycle: TierCycle;
  downgradePolicy: TierDowngradePolicy;
  keepThresholdRatio: number;
  effectiveFrom: string;
  isPublished: boolean;
  isCurrentlyEffective: boolean;
  note: string | null;
  createdAt: string;
  /** Bảng màu hạng cho phép — nguồn duy nhất của ô chọn màu. */
  allowedColors: TierColor[];
}

export interface TierImpactRow {
  code: string;
  name: string | null;
  memberCount: number;
  stillExists: boolean;
  movesTo: string | null;
}

export interface TierImpactReport {
  versionId: string;
  version: string;
  membersWithTier: number;
  byTier: TierImpactRow[];
  membersMoved: number;
  upgradeOnNextTransaction: number;
  downgradeIfCycleEndedNow: number;
  needsAcknowledgement: boolean;
  summary: string;
}

export interface CreateTierDraftPayload {
  version: string;
  levels: TierLevel[];
  cycle: TierCycle;
  downgradePolicy: TierDowngradePolicy;
  keepThresholdRatio: number;
  effectiveFrom: string;
  note: string | null;
}

export interface UpdateTierDraftPayload {
  levels: TierLevel[];
  cycle: TierCycle;
  downgradePolicy: TierDowngradePolicy;
  keepThresholdRatio: number;
  effectiveFrom: string;
  note: string | null;
}

export const tierKeys = {
  all: ['tier-definitions'] as const,
  impact: (id: string) => ['tier-impact', id] as const,
};

/**
 * Ba hạng theo chính sách thành viên PL ban hành 2026-09-11. Phải khớp
 * `TierDefinitionBootstrapper` — mã hạng khoá theo `code`, không phải tên hiển thị.
 *
 * 🔴 `threshold` là ĐIỂM XÉT HẠNG, không phải tiền. Chính sách nói theo chi tiêu năm
 * (Silver 0–19.999tr · Gold 20tr · Diamond >50tr) còn engine so với `tier_points`, nên quy đổi
 * qua `VndPerPoint = 10.000đ`: Gold 20tr → 2.000 điểm · Diamond 50tr → 5.000 điểm. Đổi tỷ giá
 * tích điểm mà quên phát hành lại thang hạng là ngưỡng lệch đúng tỷ lệ đó, và không có lỗi nào
 * báo ra.
 *
 * `discountPercent` — PL chốt 2026-09-11: Gold 5% · Diamond 10% (BR-308), đúng cặp số của tờ trình
 * `050MKT_CTKM`. Dùng được vì ngưỡng nay TRÙNG chương trình POS; lý do cũ để 0% (ngưỡng thấp hơn
 * 2,5 lần) đã hết. Hệ thống không áp giảm giá — POS áp; số này để thu ngân biết nhập bao nhiêu.
 */
export const SEED_TIER_LEVELS: TierLevel[] = [
  { code: 'silver', name: 'Silver', threshold: 0, queuePriority: false, discountPercent: 0, benefits: null },
  { code: 'gold', name: 'Gold', threshold: 2000, queuePriority: false, discountPercent: 5, benefits: null },
  { code: 'diamond', name: 'Diamond', threshold: 5000, queuePriority: false, discountPercent: 10, benefits: null },
];

export const CYCLE_LABELS: Record<TierCycle, string> = {
  Lifetime: 'Trọn đời, không reset',
  CalendarYear: 'Năm dương lịch (01/01–31/12)',
  SixMonths: '6 tháng gần nhất (theo từng khách)',
  TwelveMonths: '12 tháng gần nhất (theo từng khách)',
};

export const DOWNGRADE_LABELS: Record<TierDowngradePolicy, string> = {
  None: 'Không bao giờ rớt hạng',
  OneLevel: 'Rớt đúng một bậc (mặc định)',
  Recalculate: 'Tính lại từ điểm trong chu kỳ',
};

export const tierApi = {
  list: () => unwrap(apiRequest<TierDefinition[]>('/admin/loyalty/tiers')),

  createDraft: (body: CreateTierDraftPayload) =>
    unwrap(apiRequest<TierDefinition>('/admin/loyalty/tiers', { method: 'POST', body })),

  updateDraft: (id: string, body: UpdateTierDraftPayload) =>
    unwrap(
      apiRequest<TierDefinition>(`/admin/loyalty/tiers/${id}`, { method: 'PUT', body }),
    ),

  /**
   * Đổi màu hạng ngay trên bản (kể cả bản đã phát hành), không tạo phiên bản mới — `S4-BE-17`.
   * `colors` theo mã hạng; `null` là về màu mặc định.
   */
  updateColors: (id: string, colors: Record<string, string | null>) =>
    unwrap(
      apiRequest<TierDefinition>(`/admin/loyalty/tiers/${id}/colors`, {
        method: 'PUT',
        body: { colors },
      }),
    ),

  previewImpact: (id: string) =>
    unwrap(apiRequest<TierImpactReport>(`/admin/loyalty/tiers/${id}/impact`)),

  publish: (id: string, acknowledgeImpact: boolean) =>
    unwrap(
      apiRequest<TierDefinition>(`/admin/loyalty/tiers/${id}/publish`, {
        method: 'POST',
        body: { acknowledgeImpact },
      }),
    ),
};

/**
 * Ba trạng thái phiên bản — giống earning rules, không gộp "chờ hiệu lực" vào "đang áp dụng".
 */
export function describeTierVersionState(
  version: TierDefinition,
  allVersions: TierDefinition[],
): { label: string; tone: 'success' | 'default' | 'warning' } {
  // 'warning' khớp chấm màu "đang soạn" trong prototype (a-tier) và banner "bản nháp"
  // (type="warning") đã dùng ở chỗ khác trong trang này.
  if (!version.isPublished) return { label: 'Đang soạn', tone: 'warning' };

  if (!version.isCurrentlyEffective) return { label: 'Chờ tới ngày hiệu lực', tone: 'warning' };

  const effective = allVersions.filter((v) => v.isCurrentlyEffective);
  const newest = effective.reduce<TierDefinition | null>(
    (best, v) => (best === null || v.effectiveFrom > best.effectiveFrom ? v : best),
    null,
  );

  return newest?.id === version.id
    ? { label: 'Đang áp dụng', tone: 'success' }
    : { label: 'Đã thay thế', tone: 'default' };
}
